import Finder from '../../../common/helpers/finder';
import { NextStepButtonType } from '../../demo/dto/FrameTooltipDTO';

class Iframe {
  private static instance: Iframe | null = null;
  private currentElement?: HTMLElement | null = null;
  private frameElement?: HTMLIFrameElement | null = null;
  private positionAnimationId: number | null = null;
  private lastPosition: { x: number; y: number; width: number; height: number } | null = null;
  private lastVisibility: boolean | null = null;
  private observer: MutationObserver | null = null;
  private paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[] = [];
  private nextStepButtonType: NextStepButtonType | null = null;
  private source: MessageEventSource | null = null;
  private targetOrigin: string | null = null;
  private constructor() {
    this.initialize();
  }

  public static getInstance(): Iframe {
    if (!Iframe.instance) {
      Iframe.instance = new Iframe();
    }
    return Iframe.instance;
  }

  private initialize(): void {
    this.setupListeners();
    this.checkRunningGuide();
  }

  private checkRunningGuide(): void {
    window.parent.postMessage(
      {
        type: 'CHECK_RUNNING_GUIDE'
      },
      '*'
    );
  }

  private addClickEvent = (shouldAddClickEvent: boolean) => {
    if (shouldAddClickEvent) {
      if (Finder.isInShadow(this.currentElement as HTMLElement)) {
        (this.currentElement?.getRootNode() as Document).addEventListener(
          'click',
          this.clickEventHandler,
          {
            capture: true
          }
        );
      } else {
        document.addEventListener('click', this.clickEventHandler, { capture: true });
      }
    }
  };

  private removeClickEvent = () => {
    if (Finder.isInShadow(this.currentElement as HTMLElement)) {
      (this.currentElement?.getRootNode() as Document).removeEventListener(
        'click',
        this.clickEventHandler,
        { capture: true }
      );
    } else {
      document.removeEventListener('click', this.clickEventHandler, { capture: true });
    }
  };

  private clickEventHandler = (e: MouseEvent) => {
    if (e.target === this.currentElement || this.currentElement?.contains(e.target as Node)) {
      e.preventDefault();
      e.stopPropagation();
      this.source?.postMessage(
        {
          type: 'GO_TO_NEXT_STEP'
        },
        {
          targetOrigin: this.targetOrigin as string
        }
      );
      if (Finder.isInShadow(this.currentElement)) {
        (this.currentElement?.getRootNode() as Document).removeEventListener(
          'click',
          this.clickEventHandler,
          { capture: true }
        );
      } else {
        document.removeEventListener('click', this.clickEventHandler, { capture: true });
      }
    }
  };

  private stopPositionMonitoring = () => {
    this.removeClickEvent();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.positionAnimationId) {
      cancelAnimationFrame(this.positionAnimationId);
      this.positionAnimationId = null;
    }
    this.lastPosition = null;
  };

  private startPositionMonitoring = () => {
    this.stopPositionMonitoring();

    let lastCheckTime = 0;
    const THROTTLE_MS = 25;
    const POSITION_THRESHOLD = 2;

    this.observer = new MutationObserver(mutations => {
      if (this.currentElement && this.currentElement.isConnected) return;
      const hasAddedNodes = mutations.some(
        mutation =>
          mutation.type === 'childList' && mutation.addedNodes && mutation.addedNodes.length > 0
      );
      if (hasAddedNodes) {
        const found = Finder.findElementByPaths(this.paths);
        if (found) {
          this.currentElement = found;
          this.sendPositionChanged();
        }
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.addClickEvent(this.nextStepButtonType === NextStepButtonType.ELEMENT);

    const checkPosition = (currentTime: number) => {
      if (!this.currentElement || !this.currentElement.isConnected) {
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      if (currentTime - lastCheckTime < THROTTLE_MS) {
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      lastCheckTime = currentTime;

      const rect = this.currentElement.getBoundingClientRect();
      const currentPosition = {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      };

      const currentVisibility = Finder.isElementVisible(this.currentElement);

      const positionChanged =
        this.lastPosition &&
        (Math.abs(currentPosition.x - this.lastPosition.x) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.y - this.lastPosition.y) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.width - this.lastPosition.width) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.height - this.lastPosition.height) > POSITION_THRESHOLD);

      const visibilityChanged = this.lastVisibility !== currentVisibility;

      if (positionChanged || visibilityChanged) {
        this.sendPositionChanged();
      }

      this.lastPosition = currentPosition;
      this.lastVisibility = currentVisibility;
      this.positionAnimationId = requestAnimationFrame(checkPosition);
    };

    this.positionAnimationId = requestAnimationFrame(checkPosition);
  };

  private sendPositionChanged = async () => {
    window.parent.postMessage(
      {
        type: 'POSITION_CHANGED'
      },
      '*'
    );
  };

  private sendElementLost = async () => {
    window.parent.postMessage(
      {
        type: 'ELEMENT_LOST'
      },
      '*'
    );
  };

  private setupListeners(): void {
    window.addEventListener('message', async event => {
      if (event.data.type === 'CHECK_RUNNING_GUIDE') {
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'CHECK_RUNNING_GUIDE'
            },
            '*'
          );
        }
      }
      if (event.data.type === 'GET_IFRAME_ELEMENT_DIMENSIONS') {
        this.source = event.source;
        this.targetOrigin = event.origin;
        const dimensions = await Finder.getOverallElementDimensionsByPath(
          event.data.paths,
          {
            onBeforeFind: () => {
              if (this.currentElement) {
                this.stopPositionMonitoring();
              }
            },
            onElementFound: (element: HTMLElement) => {
              this.currentElement = element;
              this.startPositionMonitoring();
            },
            onElementLost: this.sendElementLost
          },
          event.data.nextStepButtonType
        );

        this.currentElement = dimensions.element;
        this.frameElement = dimensions.frameElement;
        this.nextStepButtonType = event.data.nextStepButtonType;

        window.parent.postMessage(
          {
            type: 'SET_IFRAME_ELEMENT_DIMENSIONS',
            dimensions: {
              x: dimensions.x,
              y: dimensions.y,
              width: dimensions.width,
              height: dimensions.height,
              isElementVisible: dimensions.isElementVisible
            }
          },
          '*'
        );
      }
      if (event.data.type === 'GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT') {
        const dimensions = await Finder.getOverallElementDimensionsByElement(
          this.currentElement as HTMLElement,
          event.data.paths
        );
        if (!dimensions)
          window.parent.postMessage(
            {
              type: 'SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT',
              dimensions: null
            },
            '*'
          );
        else
          window.parent.postMessage(
            {
              type: 'SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT',
              dimensions: {
                x: dimensions.x,
                y: dimensions.y,
                width: dimensions.width,
                height: dimensions.height,
                isOutOfViewport: dimensions.isOutOfViewport,
                isElementVisible: dimensions.isElementVisible
              }
            },
            '*'
          );
      }
      if (event.data.type === 'POSITION_CHANGED') {
        this.sendPositionChanged();
      }
      if (event.data.type === 'ELEMENT_LOST') {
        this.sendElementLost();
      }
      if (event.data.type === 'SCROLL_TO_ELEMENT') {
        this.currentElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
        this.frameElement?.contentWindow?.postMessage(
          {
            type: 'SCROLL_TO_ELEMENT'
          },
          '*'
        );
      }
      if (event.data.type === 'GO_TO_NEXT_STEP') {
        window.parent.postMessage({
          type: 'GO_TO_NEXT_STEP'
        });
      }
      if (event.data.type === 'GO_TO_NEXT_STEP_SUCCESS') {
        const { frameElement } = Finder.splitPathsByIframe(this.paths);
        if (frameElement) {
          frameElement.contentWindow?.postMessage(
            {
              type: 'GO_TO_NEXT_STEP_SUCCESS'
            },
            '*'
          );
        } else {
          this.currentElement?.click();
        }
      }
    });
  }
}

export default Iframe;
